import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { Device, SimulateUplinkRequest } from "@/contracts";

import { queryKeys, simulateUplinkMutation } from "@/api/queries";
import { useApiClient } from "@/lib/api-context";
import { Alert } from "@/components/ui/alert";
import { TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";

interface SimulateUplinkDialogProps {
  device: Device;
  open: boolean;
  onClose: () => void;
}

/**
 * Injects one uplink through the real ingest path (non-production only).
 *
 * Empty numeric fields mean "use the simulator defaults" — healthy freezer
 * readings — so a first click proves the pipeline without typing numbers.
 */
export function SimulateUplinkDialog({ device, open, onClose }: SimulateUplinkDialogProps) {
  const { t } = useTranslation("devices");
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [temperature, setTemperature] = useState("");
  const [humidity, setHumidity] = useState("");
  const [battery, setBattery] = useState("");
  const [current, setCurrent] = useState("");
  const measuresCurrent = device.type === "CURRENT";
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    ...simulateUplinkMutation(client),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.device(device.id) }),
        queryClient.invalidateQueries({ queryKey: ["devices", "telemetry", device.id] }),
        queryClient.invalidateQueries({ queryKey: ["devices", "telemetry-series", device.id] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboard }),
      ]);
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    const body: SimulateUplinkRequest = { devEui: device.devEui };
    const temperatureC = parseOptionalNumber(temperature);
    const humidityPercent = parseOptionalNumber(humidity);
    const batteryPercent = parseOptionalInt(battery);
    const currentA = parseOptionalNumber(current);

    if (measuresCurrent) {
      // A blank field still sends a modest load, so the first click exercises
      // the current path instead of the freezer defaults.
      body.currentA = currentA ?? 12;
      if (temperatureC !== undefined) {
        body.temperatureC = temperatureC;
      }
    } else {
      if (temperatureC !== undefined) {
        body.temperatureC = temperatureC;
      }
      if (humidityPercent !== undefined) {
        body.humidityPercent = humidityPercent;
      }
      if (batteryPercent !== undefined) {
        body.batteryPercent = batteryPercent;
      }
    }

    mutation.mutate(body);
  }

  return (
    <FormDialog
      open={open}
      title={t("simulate.title")}
      description={t("simulate.description")}
      submitLabel={t("simulate.submit")}
      pending={mutation.isPending}
      onSubmit={onSubmit}
      onCancel={onClose}
    >
      {error ? (
        <Alert tone="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-3">
        {measuresCurrent ? (
          <TextField
            label={t("table.current")}
            hint={t("simulate.currentHint")}
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            inputMode="decimal"
          />
        ) : null}
        <TextField
          label={measuresCurrent ? t("cableTemperature") : t("table.temperature")}
          hint={t("simulate.optionalHint")}
          value={temperature}
          onChange={(event) => setTemperature(event.target.value)}
          inputMode="decimal"
        />
        {measuresCurrent ? null : (
          <TextField
            label={t("table.humidity")}
            hint={t("simulate.optionalHint")}
            value={humidity}
            onChange={(event) => setHumidity(event.target.value)}
            inputMode="decimal"
          />
        )}
        {measuresCurrent ? null : (
          <TextField
            label={t("table.battery")}
            hint={t("simulate.optionalHint")}
            value={battery}
            onChange={(event) => setBattery(event.target.value)}
            inputMode="numeric"
          />
        )}
      </div>
    </FormDialog>
  );
}

function parseOptionalNumber(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parseOptionalInt(value: string): number | undefined {
  const parsed = parseOptionalNumber(value);
  return parsed === undefined ? undefined : Math.round(parsed);
}
